import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PAGE_SLUGS } from "@/i18n/pages";
import { Reveal, Overline } from "@/components/landing/primitives";

const StageCard = ({ item, index, slug, big = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: index * 0.08 }}
    className={big ? "lg:col-span-3" : ""}
  >
    <Link
      to={`/${slug}`}
      className={`group relative flex h-full flex-col justify-between overflow-hidden border border-white/10 bg-[#121212] p-8 transition-colors duration-300 hover:border-brand-green ${
        big ? "min-h-[320px]" : "min-h-[380px]"
      }`}
      data-testid={`solution-card-${item.tag}`}
    >
      {big && (
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1713212727531-51e120bd416a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxkeW5vJTIwdHVuaW5nfGVufDB8fHx8MTc4NDMyNzczOXww&ixlib=rb-4.1.0&q=85"
            alt="Dyno Pro fékpad tuning"
            className="h-full w-full object-cover opacity-25 transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/85 to-[#121212]/40" />
        </div>
      )}

      <div className="relative z-10 flex items-start justify-between">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-green">
          {item.tag}
        </span>
        <ArrowUpRight className="h-6 w-6 text-white/20 transition-colors group-hover:text-brand-green" />
      </div>

      <div className="relative z-10 mt-auto">
        <h3 className="font-display text-4xl font-black text-white md:text-5xl">
          {item.name}
        </h3>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
          {item.desc}
        </p>
        <ul className={`mt-6 flex flex-wrap gap-x-6 gap-y-2 ${big ? "max-w-lg" : ""}`}>
          {item.points.map((p) => (
            <li key={p} className="flex items-center gap-2 font-mono text-xs text-white/70">
              <Check className="h-3.5 w-3.5 text-brand-green" />
              {p}
            </li>
          ))}
        </ul>
      </div>
    </Link>
  </motion.div>
);

const Solutions = () => {
  const { t } = useLang();
  const s = t.solutions;
  // items order: Stage 1, Stage 2, Stage 3, Dyno Pro -> page keys
  const keys = ["stage1", "stage2", "stage3", "dyno"];

  return (
    <section
      id="solutions"
      className="relative bg-[#0A0A0A] px-5 py-24 md:px-10 md:py-32"
      data-testid="solutions-section"
    >
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <Overline>{s.overline}</Overline>
          <h2 className="mt-5 max-w-3xl font-display text-5xl font-black leading-[0.95] text-white md:text-7xl">
            {s.title}
          </h2>
          <p className="mt-6 max-w-xl text-base text-white/60 md:text-lg">
            {s.subtitle}
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-3">
          {s.items.slice(0, 3).map((item, i) => (
            <StageCard key={item.tag} item={item} index={i} slug={PAGE_SLUGS[keys[i]]} />
          ))}
          <StageCard item={s.items[3]} index={3} slug={PAGE_SLUGS.dyno} big />
        </div>
      </div>
    </section>
  );
};

export default Solutions;
